const STORAGE_KEY = "mittelalte.foliage-occlusions.v1";

// Painted canvas dimensions (matches paintedPath() in maps.ts / PathOverrides.ts).
const PAINTED_WIDTH = 768;
const PAINTED_HEIGHT = 432;

/** Circular hide-zone. Normalized 0..1; `r` is a fraction of the canvas WIDTH. */
export interface FoliageSpot {
  x: number;
  y: number;
  r: number;
}

/** Free-form polygon hide-zone, drawn like a path. Normalized 0..1 points. */
export interface FoliageArea {
  points: [number, number][];
}

/**
 * A zone where the painted background art is redrawn above towers and enemies,
 * so units standing there look like they're behind the painted foliage.
 */
export type FoliageZone = FoliageSpot | FoliageArea;

export function isFoliageArea(zone: FoliageZone): zone is FoliageArea {
  return Array.isArray((zone as FoliageArea).points);
}

type StoredOcclusions = Record<string, FoliageZone[]>;

function readOcclusions(): StoredOcclusions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as StoredOcclusions) : {};
  } catch (e) {
    console.warn("[foliage-editor] could not read occlusions", e);
    return {};
  }
}

function writeOcclusions(o: StoredOcclusions): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
  } catch (e) {
    console.warn("[foliage-editor] could not save occlusions", e);
  }
}

function isPoint(p: unknown): p is [number, number] {
  return Array.isArray(p) && p.length === 2 && typeof p[0] === "number" && typeof p[1] === "number";
}

function isZone(z: unknown): z is FoliageZone {
  if (!z || typeof z !== "object") return false;
  const area = z as FoliageArea;
  if (Array.isArray(area.points)) return area.points.length >= 3 && area.points.every(isPoint);
  const spot = z as FoliageSpot;
  return typeof spot.x === "number" && typeof spot.y === "number" && typeof spot.r === "number";
}

function isZoneArray(v: unknown): v is FoliageZone[] {
  return Array.isArray(v) && v.every(isZone);
}

export function loadFoliageOcclusions(mapKey: string): FoliageZone[] | null {
  const raw = readOcclusions()[mapKey];
  return isZoneArray(raw) ? raw : null;
}

export function saveFoliageOcclusions(mapKey: string, zones: FoliageZone[]): void {
  const all = readOcclusions();
  all[mapKey] = zones;
  writeOcclusions(all);
}

export function clearFoliageOcclusions(mapKey: string): void {
  const all = readOcclusions();
  delete all[mapKey];
  writeOcclusions(all);
}

/**
 * Paste-ready snippet for every stored override, in the same 768x432 painted
 * pixel space as paintedPath(), for `MapDef.foliageOcclusions` via
 * paintedSpot() / paintedArea() in maps.ts. Returns null when nothing is stored.
 */
export function exportCustomFoliage(): string | null {
  const all = readOcclusions();
  const keys = Object.keys(all);
  if (keys.length === 0) return null;

  const round = (n: number) => Math.round(n * 10) / 10;
  const fmtZone = (z: FoliageZone) =>
    isFoliageArea(z)
      ? `paintedArea(${z.points.map(([x, y]) => `[${round(x * PAINTED_WIDTH)}, ${round(y * PAINTED_HEIGHT)}]`).join(", ")})`
      : `paintedSpot(${round(z.x * PAINTED_WIDTH)}, ${round(z.y * PAINTED_HEIGHT)}, ${round(z.r * PAINTED_WIDTH)})`;

  return keys
    .map(key => `  // ${key}\n  foliageOcclusions: [\n${all[key].map(z => `    ${fmtZone(z)},`).join("\n")}\n  ],`)
    .join("\n\n");
}
